import { useState } from 'react';
import { Alert, Button, CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { billingService } from '../../services/admin';

/**
 * Shown globally under the header when the tenant is on Stripe billing and the
 * subscription isn't active yet (pending) or has payment issues (past_due).
 * Clicking the CTA opens a Stripe Customer Portal session so the user can add
 * a payment method, view invoices, change card, or cancel.
 */
export default function BillingBanner() {
  const { tenant } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!tenant) return null;
  if (tenant.billingMode !== 'stripe') return null;

  const status = tenant.billingStatus;
  const hasCustomer = Boolean(tenant.stripeCustomerId);

  const showForPending = status === 'pending';
  const showForPastDue = status === 'past_due';
  const showForSuspended = status === 'suspended';
  const showForCancelled = status === 'cancelled';

  if (!showForPending && !showForPastDue && !showForSuspended && !showForCancelled) {
    return null;
  }

  const severity =
    status === 'past_due' || status === 'suspended' || status === 'cancelled'
      ? 'error'
      : 'warning';

  const message = (() => {
    switch (status) {
      case 'pending':
        return 'Votre abonnement n\u2019est pas encore actif. Finalisez le paiement pour d\u00e9bloquer toutes les fonctionnalit\u00e9s.';
      case 'past_due':
        return 'Un paiement a \u00e9chou\u00e9. Mettez \u00e0 jour votre moyen de paiement pour \u00e9viter la suspension.';
      case 'suspended':
        return 'Votre compte est suspendu suite \u00e0 un paiement non r\u00e9gl\u00e9. R\u00e9glez votre abonnement pour r\u00e9activer l\u2019acc\u00e8s.';
      case 'cancelled':
        return 'Votre abonnement a \u00e9t\u00e9 annul\u00e9. Contactez-nous pour le r\u00e9activer.';
      default:
        return '';
    }
  })();

  const openPortal = async () => {
    setError(null);
    setLoading(true);
    try {
      const { url } = await billingService.createPortalSession(window.location.href);
      window.location.href = url;
    } catch (err: any) {
      setError(err?.message || 'Impossible d\u2019ouvrir le portail Stripe.');
      setLoading(false);
    }
  };

  const canOpenPortal = hasCustomer && status !== 'cancelled';

  return (
    <Alert
      severity={severity}
      sx={{ borderRadius: 0 }}
      action={
        canOpenPortal ? (
          <Button
            color="inherit"
            size="small"
            onClick={openPortal}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            G\u00e9rer mon abonnement
          </Button>
        ) : undefined
      }
    >
      {error ? error : message}
    </Alert>
  );
}
